import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ACTION_TYPES } from '@/constants';
import {
  updateTask as manhattanUpdateTask,
  getTrailerListForTask as manhattanGetTrailerList,
  getLocationListForTask as manhattanGetLocationList,
} from '@/service/ManhattanService';
import { buildUpdateTaskTransmissionHeader } from '@/service/taskService';
import { useAppSelector } from '@/store';
import { formatPSTDate } from '@/utils';

import { addToOfflineQueue, setInProgressTaskStorage } from '../offlineTaskQueue';
import { dashboardQueryKeys } from '../queries';

import type {
  CompleteFlowAnswers,
  TaskDetail,
  UpdateTaskRequestModel,
  UpdateTaskResponseModel,
  TrailerInfo,
  LocationInfo,
} from '@/types';

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────

/**
 * Build update-task request matching Xamarin YMSHelperMethods.GetUpdateTaskRequestModel().
 */
function buildUpdateRequest(
  actionType: string,
  task: TaskDetail,
  orgId: string,
  userId: string,
  plantId: string,
  sessionId: string,
  extra?: {
    ExceptionCode?: string | undefined;
    ExceptionText?: string | undefined;
    AlternateTrailerName?: string | undefined;
    AlternateCarrier?: string | undefined;
    AlternateDestinationZone?: string | undefined;
    AlternateDestinationName?: string | undefined;
    AlternateTrailerValid?: string | undefined;
    OverrideReasonCode?: string | undefined;
  }
): UpdateTaskRequestModel {
  return {
    Payload: {
      TransmissionHeader: buildUpdateTaskTransmissionHeader(),
      Body: {
        OrgId: orgId,
        StopOnFirstActionError: 'Y',
        UpdateTaskDetails: [
          {
            TaskType: task.TaskType,
            OrgId: orgId,
            TaskId: task.TaskId,
            UserId: userId,
            ActionType: actionType,
            ActionDatetime: formatPSTDate('datetime'),
            TaskComments: task.TaskComments,
            TrailerId: task.TrailerId,
            TrailerName: task.TrailerName,
            TrailerComments: task.TrailerComments,
            CarrierCompanyName: task.CarrierCompanyName,
            CarrierName: task.CarrierName,
            ...extra,
          },
        ],
      },
    },
    TraceData: {
      User: userId,
      Plant: plantId,
      SessionId: sessionId,
    },
  };
}

// ─────────────────────────────────────────────────────────────────
// Mutation Hooks
// ─────────────────────────────────────────────────────────────────

export const useAcceptTaskMutation = () => {
  const queryClient = useQueryClient();
  const orgId = useAppSelector((state) => String(state.user.plant?.org_id ?? ''));
  // Matches Xamarin: UserId = countryCode + mobileNo
  const userId = useAppSelector(
    (state) => `${state.user.user?.country_code ?? ''}${state.user.user?.mobile_no ?? ''}`
  );
  const plantId = useAppSelector((state) => state.user.plant?._id ?? '');
  const sessionId = useAppSelector((state) => state.user.sessionId ?? '');

  return useMutation({
    mutationFn: async (task: TaskDetail) => {
      const request = buildUpdateRequest(
        ACTION_TYPES.ACCEPT,
        task,
        orgId,
        userId,
        plantId,
        sessionId
      );
      return manhattanUpdateTask(request);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: dashboardQueryKeys.upcomingTasks(orgId) });
      void queryClient.invalidateQueries({
        queryKey: dashboardQueryKeys.myTasks(orgId, userId),
      });
    },
  });
};

export const useStartTaskMutation = () => {
  const queryClient = useQueryClient();
  const orgId = useAppSelector((state) => String(state.user.plant?.org_id ?? ''));
  const userId = useAppSelector(
    (state) => `${state.user.user?.country_code ?? ''}${state.user.user?.mobile_no ?? ''}`
  );
  const userName = useAppSelector((state) =>
    `${state.user.user?.first_name ?? ''} ${state.user.user?.last_name ?? ''}`.trim()
  );
  const plantId = useAppSelector((state) => state.user.plant?._id ?? '');
  const sessionId = useAppSelector((state) => state.user.sessionId ?? '');

  return useMutation({
    // Allow mutation to run when offline — our mutationFn handles offline logic internally
    networkMode: 'always',
    mutationFn: async (task: TaskDetail): Promise<UpdateTaskResponseModel> => {
      const request = buildUpdateRequest(
        ACTION_TYPES.START,
        task,
        orgId,
        userId,
        plantId,
        sessionId
      );

      // Online — normal API call
      if (navigator.onLine) {
        return manhattanUpdateTask(request);
      }

      // Offline — queue the update and return a fake response
      // Matches Xamarin: MiddlewareService.UpdateTaskAsync offline branch + OfflineStartActionResponse
      const updateDetail = request.Payload.Body.UpdateTaskDetails[0];
      if (updateDetail) {
        addToOfflineQueue(updateDetail);
      }

      // Build offline in-progress response matching Xamarin YMSHelperMethods.OfflineStartActionResponse
      const offlineInProgressTask: TaskDetail = {
        ...task,
        Status: 40, // In Progress
        TaskStartDateTime: new Date().toISOString(),
        AssignedUserName: userName,
      };

      // Optimistically update the query cache:
      // 1. Remove from My Tasks list
      queryClient.setQueryData<TaskDetail[]>(dashboardQueryKeys.myTasks(orgId, userId), (old) =>
        old ? old.filter((t) => t.TaskId !== task.TaskId) : []
      );
      // 2. Remove from Upcoming list (task may have been started directly from upcoming tab)
      queryClient.setQueryData<TaskDetail[]>(dashboardQueryKeys.upcomingTasks(orgId), (old) =>
        old ? old.filter((t) => t.TaskId !== task.TaskId) : []
      );
      // 3. Set as in-progress task
      queryClient.setQueryData<TaskDetail>(
        dashboardQueryKeys.inProgressTask(orgId, userId),
        offlineInProgressTask
      );

      return {
        Payload: {
          Body: {
            UpdateTaskDetails: [offlineInProgressTask],
          },
        },
      };
    },
    onSuccess: (_data, task) => {
      // Only invalidate if online — offline already set cache optimistically above
      if (navigator.onLine) {
        // Matches Xamarin StartTask (line 777-778):
        //   inProgressTask.TaskStartedDateTime = DateTime.Now.ToString();
        //   InprogressTask = inProgressTask;
        // Xamarin sets TaskStartedDateTime to local NOW (not the server's time) and
        // does NOT refetch the in-progress task — so the timer always starts from 00:00.
        const startedTask: TaskDetail = {
          ...task,
          Status: 40, // In Progress
          TaskStartDateTime: new Date().toISOString(),
          AssignedUserName: userName,
        };
        setInProgressTaskStorage(startedTask);
        queryClient.setQueryData<TaskDetail | null>(
          dashboardQueryKeys.inProgressTask(orgId, userId),
          startedTask
        );

        // Invalidate task lists — but NOT inProgressTask, because our optimistic data
        // with local DateTime.Now is correct. Refetching would overwrite it with the
        // server's TaskStartDatetime (which may differ), causing the timer to jump.
        void queryClient.invalidateQueries({
          queryKey: dashboardQueryKeys.myTasks(orgId, userId),
        });
        void queryClient.invalidateQueries({
          queryKey: dashboardQueryKeys.upcomingTasks(orgId),
        });
      }
    },
  });
};

export const useCompleteTaskMutation = () => {
  const queryClient = useQueryClient();
  const orgId = useAppSelector((state) => String(state.user.plant?.org_id ?? ''));
  const userId = useAppSelector(
    (state) => `${state.user.user?.country_code ?? ''}${state.user.user?.mobile_no ?? ''}`
  );
  const userName = useAppSelector((state) =>
    `${state.user.user?.first_name ?? ''} ${state.user.user?.last_name ?? ''}`.trim()
  );
  const plantId = useAppSelector((state) => state.user.plant?._id ?? '');
  const sessionId = useAppSelector((state) => state.user.sessionId ?? '');

  return useMutation({
    // Allow mutation to run when offline — our mutationFn handles offline logic internally
    networkMode: 'always',
    mutationFn: async ({
      task,
      answers,
    }: {
      task: TaskDetail;
      answers: CompleteFlowAnswers;
    }): Promise<UpdateTaskResponseModel> => {
      // Build alternate trailer/carrier — matches Xamarin CompleteCommandHandler
      let alternateTrailerName: string | undefined;
      let alternateCarrier: string | undefined;
      let alternateTrailerValid: string | undefined;

      if (answers.selectedTrailer && !answers.isOtherTrailer) {
        alternateTrailerName = answers.selectedTrailer.TrailerName;
        alternateCarrier = answers.selectedTrailer.CarrierName;
        alternateTrailerValid = 'true';
      } else if (answers.isOtherTrailer) {
        alternateTrailerName = answers.otherTrailerText;
        alternateCarrier = answers.otherCarrierText;
        alternateTrailerValid = 'false';
      } else {
        alternateTrailerName = answers.alternateTrailerName ?? '';
        alternateCarrier = answers.alternateCarrier ?? '';
        alternateTrailerValid = answers.alternateTrailerValid ?? 'false';
      }

      const extra = {
        ExceptionCode: answers.exceptionCode,
        ExceptionText: answers.exceptionText,
        AlternateTrailerName: alternateTrailerName,
        AlternateCarrier: alternateCarrier,
        AlternateTrailerValid: alternateTrailerValid,
        AlternateDestinationZone:
          answers.selectedLocation?.LocationZone ?? answers.alternateDestinationZone,
        AlternateDestinationName:
          answers.selectedLocation?.LocationName ?? answers.alternateDestinationName,
        OverrideReasonCode: answers.overrideReasonCode,
      };

      // Apply renamed trailer to task — matches Xamarin: CurrentTask.TrailerName = TrailerNumber
      const taskWithUpdates: TaskDetail = {
        ...task,
        TrailerName: answers.renamedTrailer ?? task.TrailerName,
        TaskComments:
          (answers.taskComments ?? task.TaskComments) + (answers.overrideReasonTaskComment ?? ''),
        TrailerComments: answers.trailerComments ?? task.TrailerComments,
      };

      // Matches Xamarin: ActionType = TaskType.ToUpper() for complete (e.g. "MOVE", "AUDIT")
      const request = buildUpdateRequest(
        task.TaskType.toUpperCase(),
        taskWithUpdates,
        orgId,
        userId,
        plantId,
        sessionId,
        extra
      );

      // Online — normal API call
      if (navigator.onLine) {
        return manhattanUpdateTask(request);
      }

      // Offline — queue the update and return a fake response
      // Matches Xamarin: MiddlewareService.UpdateTaskAsync offline branch for MOVE/AUDIT action
      const updateDetail = request.Payload.Body.UpdateTaskDetails[0];
      if (updateDetail) {
        addToOfflineQueue(updateDetail);
      }

      // Build offline completed response matching Xamarin OfflineCompletedTaskResponse
      const offlineCompletedTask: TaskDetail = {
        ...task,
        Status: 60, // Completed
        TaskEndDateTime: new Date().toISOString(),
        LastUpdatedDatetime: formatPSTDate('datetime'), // Ensure task passes session filter
        AssignedUserName: userName,
        ExceptionCode: answers.exceptionCode ?? task.ExceptionCode,
        ExceptionText: answers.exceptionText ?? task.ExceptionText,
      };

      // Optimistically update the query cache:
      // 1. Clear in-progress task
      queryClient.setQueryData<TaskDetail | null>(
        dashboardQueryKeys.inProgressTask(orgId, userId),
        null
      );
      // 2. Add to completed list
      queryClient.setQueryData<TaskDetail[]>(
        dashboardQueryKeys.completedTasks(orgId, userId),
        (old) => (old ? [offlineCompletedTask, ...old] : [offlineCompletedTask])
      );

      return {
        Payload: {
          Body: {
            UpdateTaskDetails: [offlineCompletedTask],
          },
        },
      };
    },
    onSuccess: () => {
      // Only invalidate if online — offline already set cache optimistically
      if (navigator.onLine) {
        // Immediately clear in-progress task — matches Xamarin OnSuccessCompleteTaskAction:
        // _timer.Stop(); InprogressTask = null; InProgressTimer = "00:00";
        // This ensures the timer resets instantly, not after the refetch completes.
        queryClient.setQueryData<TaskDetail | null>(
          dashboardQueryKeys.inProgressTask(orgId, userId),
          null
        );

        void queryClient.invalidateQueries({
          queryKey: dashboardQueryKeys.inProgressTask(orgId, userId),
        });
        void queryClient.invalidateQueries({
          queryKey: dashboardQueryKeys.completedTasks(orgId, userId),
        });
      }
    },
  });
};

export const useCancelTaskMutation = () => {
  const queryClient = useQueryClient();
  const orgId = useAppSelector((state) => String(state.user.plant?.org_id ?? ''));
  const userId = useAppSelector(
    (state) => `${state.user.user?.country_code ?? ''}${state.user.user?.mobile_no ?? ''}`
  );
  const plantId = useAppSelector((state) => state.user.plant?._id ?? '');
  const sessionId = useAppSelector((state) => state.user.sessionId ?? '');

  return useMutation({
    mutationFn: async (task: TaskDetail) => {
      const request = buildUpdateRequest(
        ACTION_TYPES.CANCEL,
        task,
        orgId,
        userId,
        plantId,
        sessionId
      );
      return manhattanUpdateTask(request);
    },
    onSuccess: () => {
      // Immediately clear in-progress task — matches Xamarin ResetInprogressTask:
      // InprogressTask = null; InProgressTimer = "00:00";
      // This ensures the timer resets instantly, not after the refetch completes.
      queryClient.setQueryData<TaskDetail | null>(
        dashboardQueryKeys.inProgressTask(orgId, userId),
        null
      );

      void queryClient.invalidateQueries({ queryKey: dashboardQueryKeys.upcomingTasks(orgId) });
      void queryClient.invalidateQueries({
        queryKey: dashboardQueryKeys.myTasks(orgId, userId),
      });
      void queryClient.invalidateQueries({
        queryKey: dashboardQueryKeys.inProgressTask(orgId, userId),
      });
    },
  });
};

export const useFetchTrailerListMutation = () => {
  const orgId = useAppSelector((state) => String(state.user.plant?.org_id ?? ''));
  const userId = useAppSelector(
    (state) => `${state.user.user?.country_code ?? ''}${state.user.user?.mobile_no ?? ''}`
  );

  return useMutation({
    mutationFn: async (taskId: string): Promise<TrailerInfo[]> => {
      return manhattanGetTrailerList(orgId, taskId, userId);
    },
  });
};

export const useFetchLocationListMutation = () => {
  const orgId = useAppSelector((state) => String(state.user.plant?.org_id ?? ''));
  const userId = useAppSelector(
    (state) => `${state.user.user?.country_code ?? ''}${state.user.user?.mobile_no ?? ''}`
  );

  return useMutation({
    mutationFn: async (taskId: string): Promise<LocationInfo[]> => {
      return manhattanGetLocationList(orgId, taskId, userId);
    },
  });
};
